import {
  ApiAlertResponse,
  ApiAlertConditionGroup,
  ApiProject,
} from "@incendium/api";
import { Box } from "@mui/material";
import ConditionGroupsFormWrapper from "Components/ConditionalGroup/ConditionGroupsFormWrapper";
import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import AlertCommsDialog from "./AlertCommsDialog";
import AlertRules from "./AlertRules";
import AlertSidebar from "./AlertSidebar";
import { useConditionGroupContext } from "Components/ConditionalGroup/ConditionGroupProvider";
import { useNotification } from "Hooks";
import { saveAlert } from "../services/alertService";
import { useAlerts } from "Hooks/useAlerts";
import produce from "immer";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";

interface IAlertFormProps {
  alert: ApiAlertResponse;
  setAlert: React.Dispatch<React.SetStateAction<ApiAlertResponse | undefined>>;
  project: ApiProject;
  isNew?: boolean;
}

function AlertForm({ alert, setAlert, project, isNew }: IAlertFormProps) {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const { conditionGroups, setConditionGroups } =
    useConditionGroupContext<ApiAlertConditionGroup>();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { setAlerts } = useAlerts();
  const { pathByName } = useProtectedRoutes();

  useEffect(() => {
    if (!alert || conditionGroups.length) {
      return;
    }
    const groups = alert.requirements?.[0]?.conditionGroups || [];
    setConditionGroups(
      groups && groups.length > 0
        ? [...groups]
        : [
            {
              conditions: [{}],
            },
          ],
    );
  }, [alert, setConditionGroups, conditionGroups]);

  const onCancel = () => {
    history.push(
      pathByName(RouteNames.ALERTS, {
        clientId: project.clientId,
        projectId: project.id,
      }),
    );
  };

  const onSubmit = useCallback(async () => {
    try {
      console.log("alert", alert);
      // although alert supports multiple requirement, currently we only support one requirement
      const res = await saveAlert(project.id as number, {
        ...alert,
        requirements: [
          { ...alert.requirements?.[0], conditionGroups: conditionGroups },
        ],
      });
      showSuccessNotification(`${res.name} Saved`);
      setAlerts((alerts) =>
        produce(alerts, (draft) => {
          if (isNew) {
            draft.push(res);
            return;
          }
          const idx = alerts.findIndex((a) => alert.id === a.id);
          if (idx !== -1) {
            draft[idx] = res;
          }
        }),
      );
      if (isNew && history.location.pathname.includes("new")) {
        history.push(
          pathByName(RouteNames.ALERT, {
            alertId: res.id,
            clientId: project.clientId,
            projectId: project.id,
          }),
        );
      }
    } catch (error) {
      console.error(error);
      showErrorNotification("Error saving alert");
    }
  }, [
    alert,
    isNew,
    project,
    setAlerts,
    showSuccessNotification,
    showErrorNotification,
    history,
    conditionGroups,
    pathByName,
  ]);

  return (
    <Box px={5}>
      <ConditionGroupsFormWrapper
        onSubmit={onSubmit}
        onCancel={onCancel}
        rules={<AlertRules />}
      >
        <AlertSidebar alert={alert} setAlert={setAlert} setOpen={setOpen} />
      </ConditionGroupsFormWrapper>
      <AlertCommsDialog
        open={open}
        setOpen={setOpen}
        alert={alert}
        setAlert={setAlert}
      />
    </Box>
  );
}

export default AlertForm;
